import { useReducer, useState, useMemo, useCallback } from 'react';

// ─── Initial State ───────────────────────────────────────────────────────────

const initialFormState = {
  name: '',
  instanceId: '',
  channel: '',
  connectorId: '',
  triggerConfig: { type: 'manual' },
  payloadSchema: null,
  resultPath: '',
  dataSource: {
    connectorId: '',
    toolName: '',
    contactField: 'telefone',
    nameField: 'nome',
    params: '{}',
    filters: [],
  },
  templateId: '',
  variables: {},
  variableMapping: {},
  buttonMappings: {},
  agentFollowUpEnabled: true,
  rateLimit: { batchSize: 30, delayMs: 0 },
  schedule: { enabled: false, mode: 'once', scheduledAt: '', cronExpression: '' },
};

// ─── Action Types ────────────────────────────────────────────────────────────

const ACTIONS = {
  SET_FIELD: 'SET_FIELD',
  SET_BULK: 'SET_BULK',
  RESET: 'RESET',
  SET_TRIGGER_TYPE: 'SET_TRIGGER_TYPE',
  SELECT_CHANNEL: 'SELECT_CHANNEL',
  SELECT_TEMPLATE: 'SELECT_TEMPLATE',
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function setNestedField(obj, path, value) {
  const keys = path.split('.');
  if (keys.length === 1) {
    return { ...obj, [keys[0]]: value };
  }
  const [first, ...rest] = keys;
  return {
    ...obj,
    [first]: setNestedField(obj[first] || {}, rest.join('.'), value),
  };
}

// ─── Reducer ─────────────────────────────────────────────────────────────────

function formReducer(state, action) {
  switch (action.type) {
    case ACTIONS.SET_FIELD: {
      const { path, value } = action.payload;
      return setNestedField(state, path, value);
    }

    case ACTIONS.SET_BULK: {
      const { fields } = action.payload;
      return { ...state, ...fields };
    }

    case ACTIONS.RESET: {
      const { data } = action.payload || {};
      return data ? { ...initialFormState, ...data } : { ...initialFormState };
    }

    case ACTIONS.SET_TRIGGER_TYPE: {
      const { triggerType } = action.payload;
      const prevType = state.triggerConfig?.type || 'manual';

      // Common base — preserve common fields
      let newState = { ...state };

      // Clear fields specific to previous trigger type
      if (prevType === 'endpoint' || prevType === 'email') {
        newState = {
          ...newState,
          payloadSchema: null,
          resultPath: '',
        };
      }
      if (prevType === 'endpoint') {
        newState = {
          ...newState,
          triggerConfig: { type: triggerType },
        };
      } else if (prevType === 'email') {
        newState = {
          ...newState,
          triggerConfig: { type: triggerType },
        };
      }

      if (prevType === 'manual' || prevType === 'scheduled') {
        newState = {
          ...newState,
          dataSource: {
            connectorId: '',
            toolName: '',
            contactField: 'telefone',
            nameField: 'nome',
            params: '{}',
            filters: [],
          },
        };
      }

      // Set new trigger config
      newState.triggerConfig = { type: triggerType };

      // Auto-enable/disable schedule based on trigger type
      if (triggerType === 'scheduled') {
        newState.schedule = { ...newState.schedule, enabled: true };
      } else {
        newState.schedule = { ...newState.schedule, enabled: false };
      }

      return newState;
    }

    case ACTIONS.SELECT_CHANNEL: {
      const { channel, connectorId } = action.payload;
      return {
        ...state,
        channel,
        connectorId: connectorId || '',
      };
    }

    case ACTIONS.SELECT_TEMPLATE: {
      const { templateId } = action.payload;
      return {
        ...state,
        templateId,
        variables: {},
        variableMapping: {},
      };
    }

    default:
      return state;
  }
}

// ─── Step Configuration ──────────────────────────────────────────────────────

const WIZARD_STEPS = [
  { key: 'basic', label: 'Básico', requiredFields: ['name', 'instanceId', 'channel'] },
  { key: 'trigger', label: 'Trigger e Dados' },
  { key: 'template', label: 'Template e Variáveis', requiredFields: ['templateId'] },
  { key: 'buttons', label: 'Botões e Respostas' },
  { key: 'summary', label: 'Envio' },
];

// ─── Step Validators ─────────────────────────────────────────────────────────

const stepValidators = {
  basic: (state) => {
    const errors = {};
    if (!state.name.trim()) errors.name = 'Nome é obrigatório';
    if (!state.instanceId) errors.instanceId = 'Selecione um agente';
    if (!state.channel) errors.channel = 'Selecione um canal';
    return errors;
  },
  trigger: (state) => {
    const errors = {};
    const type = state.triggerConfig?.type || 'manual';
    if (type === 'endpoint' && !state.triggerConfig?.verifyToken) {
      errors.verifyToken = 'Gere um Verify Token';
    }
    if (type === 'email' && (!state.triggerConfig?.authorizedEmails?.length)) {
      errors.authorizedEmails = 'Adicione ao menos um email autorizado';
    }
    if ((type === 'manual' || type === 'scheduled') && !state.dataSource.connectorId) {
      errors['dataSource.connectorId'] = 'Selecione um conector de dados';
    }
    if ((type === 'manual' || type === 'scheduled') && !state.dataSource.toolName) {
      errors['dataSource.toolName'] = 'Selecione uma função';
    }
    // Validate params JSON
    if (type === 'manual' || type === 'scheduled') {
      const raw = (state.dataSource.params || '').trim();
      if (raw && raw !== '{}') {
        try { JSON.parse(raw); } catch {
          errors['dataSource.params'] = 'JSON inválido nos parâmetros';
        }
      }
    }
    // Validate schedule fields for scheduled trigger
    if (type === 'scheduled') {
      const schedule = state.schedule || {};
      const mode = schedule.mode || 'once';
      if (mode === 'once' && !schedule.scheduledAt) {
        errors['schedule.scheduledAt'] = 'Informe a data e hora do disparo';
      }
      if (mode === 'once' && schedule.scheduledAt && new Date(schedule.scheduledAt) <= new Date()) {
        errors['schedule.scheduledAt'] = 'A data deve ser futura';
      }
      if (mode === 'recurrent' && !schedule.cronExpression) {
        errors['schedule.cronExpression'] = 'Informe a expressão cron ou rate';
      }
    }
    return errors;
  },
  template: (state) => {
    const errors = {};
    if (!state.templateId) errors.templateId = 'Selecione um template';
    return errors;
  },
  buttons: () => ({}),
  summary: () => ({}),
};

// ─── Hook ────────────────────────────────────────────────────────────────────

export function useWizardForm(initialData = null) {
  const [state, dispatch] = useReducer(
    formReducer,
    initialData ? { ...initialFormState, ...initialData } : { ...initialFormState }
  );

  const [currentStep, setCurrentStep] = useState(0);

  const validateStep = useCallback((stepIndex) => {
    const step = WIZARD_STEPS[stepIndex];
    if (!step) return {};
    const validator = stepValidators[step.key];
    if (!validator) return {};
    return validator(state);
  }, [state]);

  const errors = useMemo(() => {
    return validateStep(currentStep);
  }, [validateStep, currentStep]);

  const canAdvance = useMemo(() => {
    return Object.keys(errors).length === 0;
  }, [errors]);

  const nextStep = useCallback(() => {
    const stepErrors = validateStep(currentStep);
    if (Object.keys(stepErrors).length > 0) {
      return false;
    }
    if (currentStep < WIZARD_STEPS.length - 1) {
      setCurrentStep((prev) => prev + 1);
      return true;
    }
    return false;
  }, [currentStep, validateStep]);

  const prevStep = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
      return true;
    }
    return false;
  }, [currentStep]);

  const setStep = useCallback((stepIndex) => {
    const clamped = Math.max(0, Math.min(stepIndex, WIZARD_STEPS.length - 1));
    setCurrentStep(clamped);
  }, []);

  return {
    state,
    dispatch,
    currentStep,
    setStep,
    nextStep,
    prevStep,
    errors,
    validateStep,
    canAdvance,
  };
}

export { ACTIONS, WIZARD_STEPS, stepValidators, initialFormState };
